import { DataSourceTable } from 'src/constants/dataSourceTable';
import { DataSourceTypeDescription } from 'src/constants/dataSourceTypeDescription';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import { dataSourceNativeQuoteCharacter } from 'src/selectors/dataSourceNativeQuoteCharacter';
import { StardogFeatureSupported } from 'src/store/features/types';
import {
  Project,
  ProjectFileUpload,
  ProjectResource,
  ProjectVirtualGraph,
} from 'src/store/state/projects';
import {
  SUGGESTIONS_CONFIGURATION_IRI,
  SUGGESTIONS_MODEL_IRI,
} from 'src/templates/queries/iris';
import { getAllProjectNamespaces } from 'src/utils/export/namespaces';
import { generateSqlQueryForTable } from 'src/utils/generateSqlQueryForTable';
import { stringifyModelConceptAttributes } from 'src/utils/stringify/modelConceptAttributes';
import { stringifyModelConceptClasses } from 'src/utils/stringify/modelConceptClasses';
import { stringifyModelConceptRelationships } from 'src/utils/stringify/modelConceptRelationships';
import { stringifyTurtleNamespaces } from 'src/utils/stringify/namespaces';
import {
  createConfigForCustomVirtualGraph,
  createConfigForFileUpload,
  createConfigForTableVirtualGraphs,
} from 'src/utils/suggestions/createSuggestionsConfig';

/**
 * Generates configs for resources.
 *
 * If there are multiple virtual-graph resources that have
 * the same connection and data-source, they are grouped together.
 */
export const createSuggestionsRequestContents = ({
  classIris,
  defaultConnectionId,
  model,
  project,
  resourceIds,
  stardogFeaturesByConnectionId,
  typeDescriptionByConnectionId,
}: {
  classIris: string[];
  /** The default connection to use when `connectionId` is null or invalid. */
  defaultConnectionId: string;
  model: ModelSnapshot;
  project: Project;
  resourceIds: string[];
  stardogFeaturesByConnectionId: Record<string, StardogFeatureSupported>;
  typeDescriptionByConnectionId: Record<string, DataSourceTypeDescription>;
}) => {
  const databaseNamespaces = getAllProjectNamespaces(project);

  const namespaces = stringifyTurtleNamespaces(databaseNamespaces);

  let modelContents = '';
  if (classIris.length) {
    const classes = stringifyModelConceptClasses(
      model,
      databaseNamespaces,
      ({ value }) => classIris.includes(value)
    );
    const attributes = stringifyModelConceptAttributes(
      model,
      project.propertyType,
      databaseNamespaces,
      ({ domains, ranges }) =>
        Object.keys(domains).some((domain) => classIris.includes(domain)) ||
        Object.keys(ranges).some((range) => classIris.includes(range)),
      classIris
    );
    const relationships = stringifyModelConceptRelationships(
      model,
      project.propertyType,
      databaseNamespaces,
      ([, { domains, ranges }]) =>
        Array.from(domains).some((domain) => classIris.includes(domain)) ||
        Array.from(ranges).some((range) => classIris.includes(range)),
      classIris
    );

    modelContents = `
<${SUGGESTIONS_MODEL_IRI}> {
${[classes, attributes, relationships].filter((str) => str).join('\n\n')}
}
`;
  }

  const fileUploadResources: ProjectFileUpload[] = [];
  const virtualGraphCustomResources: Array<{
    connectionId: string;
    query: string;
    virtualGraph: ProjectVirtualGraph;
  }> = [];
  /** grouped by connectionId, then by dataSourceId */
  const virtualGraphTableResources: Record<
    string,
    Record<
      string,
      Array<{
        table: DataSourceTable;
        virtualGraph: ProjectVirtualGraph;
      }>
    >
  > = {};

  resourceIds.forEach((resourceId) => {
    const fileUpload = project.fileUploadsById[resourceId];
    const virtualGraph = project.virtualGraphsById[resourceId];

    if (fileUpload) {
      fileUploadResources.push(fileUpload);
    } else if (virtualGraph) {
      const connectionId = virtualGraph.connectionId || defaultConnectionId;
      const stardogFeatures =
        stardogFeaturesByConnectionId[connectionId] ||
        stardogFeaturesByConnectionId[defaultConnectionId];
      const typeDescription =
        typeDescriptionByConnectionId[connectionId] ||
        typeDescriptionByConnectionId[defaultConnectionId];

      if (typeof virtualGraph.from === 'string') {
        virtualGraphCustomResources.push({
          connectionId,
          query: virtualGraph.from,
          virtualGraph,
        });
      } else if (
        !stardogFeatures.isAutoMappingMultipleTablesTogetherSupported
      ) {
        const { from: table } = virtualGraph;
        // fallback to generating sql queries for table names,
        // and only send 1 table name per suggestions request
        // (since we don't support AutoMappingMultipleTablesTogether)
        //
        // we do this instead of using `sourceTable` because before
        // stardog 8.2.1 `sourceTable` didn't accept qualified table
        // names including catalog, schema, or data source-specific
        // quote characters; whereas custom sql queries can use them
        const nativeQuoteCharacter = dataSourceNativeQuoteCharacter(
          typeDescription,
          virtualGraph.dataSourceType
        );
        const query = generateSqlQueryForTable({
          table,
          nativeQuoteCharacter,
          options: virtualGraph.options,
          limit: true,
        });

        virtualGraphCustomResources.push({
          connectionId: connectionId || defaultConnectionId,
          query,
          virtualGraph,
        });
      } else {
        const { dataSourceId, from: table } = virtualGraph;

        let byConnectionId = virtualGraphTableResources[connectionId];
        if (!byConnectionId) {
          byConnectionId = {};
          virtualGraphTableResources[connectionId] = byConnectionId;
        }
        let byDataSourceId = byConnectionId[dataSourceId];
        if (!byDataSourceId) {
          byDataSourceId = [];
          byConnectionId[dataSourceId] = byDataSourceId;
        }

        byDataSourceId.push({
          table,
          virtualGraph,
        });
      }
    } else {
      console.warn("Couldn't find resource in project", { resourceId });
    }
  });

  const configs: Array<{
    config: string;
    connectionId: string | null;
    resource: ProjectResource | null;
    tableNameToResourceMap: Record<string, ProjectResource> | null;
  }> = [];
  const sourceToResourceIdMap: Record<string, string> = {};

  fileUploadResources.forEach((fileUpload) => {
    sourceToResourceIdMap[fileUpload.name] = fileUpload.id;
    configs.push({
      config: createConfigForFileUpload(
        project,
        fileUpload,
        classIris,
        stardogFeaturesByConnectionId[defaultConnectionId]
      ),
      connectionId: null,
      resource: fileUpload,
      tableNameToResourceMap: null,
    });
  });

  virtualGraphCustomResources.forEach(
    ({ connectionId, query, virtualGraph }) => {
      const stardogFeatures =
        stardogFeaturesByConnectionId[connectionId] ||
        stardogFeaturesByConnectionId[defaultConnectionId];

      configs.push({
        config: createConfigForCustomVirtualGraph(
          project,
          virtualGraph,
          query,
          classIris,
          stardogFeatures
        ),
        connectionId,
        resource: virtualGraph,
        tableNameToResourceMap: null,
      });
    }
  );

  Object.entries(virtualGraphTableResources).forEach(
    ([connectionId, byDataSourceId]) => {
      const stardogFeatures =
        stardogFeaturesByConnectionId[connectionId] ||
        stardogFeaturesByConnectionId[defaultConnectionId];

      Object.entries(byDataSourceId).forEach(([dataSourceId, grouped]) => {
        const tableNames: string[] = [];
        const tableNameToResourceMap: Record<string, ProjectResource> = {};
        grouped.forEach(
          ({ table: { catalog, name, schema }, virtualGraph }) => {
            const tableName = [catalog, schema, name].filter(Boolean).join('.');
            tableNames.push(tableName);
            tableNameToResourceMap[tableName] = virtualGraph;
          }
        );

        configs.push({
          config: createConfigForTableVirtualGraphs(
            project,
            dataSourceId,
            tableNames,
            classIris,
            stardogFeatures
          ),
          connectionId,
          resource: null,
          tableNameToResourceMap,
        });
      });
    }
  );

  return configs.map(
    ({ config, connectionId, resource, tableNameToResourceMap }) => {
      const content = `
${namespaces}

<${SUGGESTIONS_CONFIGURATION_IRI}> {
  [] ${config.trim()}
}
${modelContents}
`.trim();

      return { connectionId, content, resource, tableNameToResourceMap };
    }
  );
};
